import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { PageLayout } from "ui/componentes/PageLayout";
import {
    Championship,
    Collections,
    convertToClasses,
    useCollection,
    useThemeBreakpoints,
} from "api";
import { useParams } from "react-router";
import { RoundStandings } from "ui/views/ChampionshipStandings/RoundStandings";
import { Box, Grid, Tab, Tabs, Typography } from "@material-ui/core";
import { FormButton, LoggedUserContext, Title } from "ui/componentes";
import { ChampionshipDriver } from "api/model/Championship/ChampionshipDriver";
import { useSnackbar } from "notistack";
import { functions } from "config/firebaseConfig";
import { useUpdateResults } from "ui/views/ChampionshipStandings/useUpdateResults";
import { DriversStandingsTable } from "ui/views/ChampionshipStandings/DriversStandingsTable";
import { TeamsStandingsTable } from "ui/views/ChampionshipStandings/TeamsStandingsTable";
import { PenaltyPointsTable } from "./PenaltyPointsTable";
import { SafeDriversTable } from "./SafeDriversTable";

const selectClassTab = (setSelectedClass) => (event, value) => {
    setSelectedClass(value);
};

interface ChampionshipStandingsProps {
    championshipId: string;
}

interface UseUpdateChampionshipResults {
    updateChampionshipResults: (championship: Championship) => void;
    uploading: boolean;
}

const useUpdateChampionshipResults = (): UseUpdateChampionshipResults => {
    const [uploading, setUploading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const updateChampionshipResults = useCallback(
        (championship: Championship) => {
            setUploading(true);
            functions
                .httpsCallable("results-calculateChampionshipStandings")({
                    championshipId: championship.id,
                })
                .then(() => {
                    enqueueSnackbar("Resultados atualizados com sucesso!", {
                        variant: "success",
                    });
                })
                .catch(() => {
                    enqueueSnackbar(
                        "Não possível atualizar a classificação deste campeonato!",
                        { variant: "error" }
                    );
                })
                .finally(() => setUploading(false));
        },
        [enqueueSnackbar]
    );

    return {
        updateChampionshipResults,
        uploading,
    };
};

const ChampionshipStandings: React.FC<ChampionshipStandingsProps> = ({
    championshipId: champId,
}) => {
    const { isDownMd } = useThemeBreakpoints();
    const [selectedClass, setSelectedClass] = useState("");
    const loggedUser = useContext(LoggedUserContext);
    const params = useParams<{ championshipId: string }>();

    const championshipId = useMemo(() => {
        return params?.championshipId || champId || "-1";
    }, [champId, params]);

    const championship =
        useCollection<Championship>({
            collection: Collections.CHAMPIONSHIPS,
            doc: championshipId,
        }) || {};

    const classes = useMemo(() => {
        if (!championship.isMultiClass) return [];
        return convertToClasses(championship.cars);
    }, [championship.cars, championship.isMultiClass]);
    useEffect(() => {
        if (!selectedClass && classes.length) {
            setSelectedClass(classes[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes]);

    const drivers = useCollection<ChampionshipDriver[]>(
        {
            collection: Collections.CHAMPIONSHIP_DRIVERS,
            where: ["championshipId", "==", championshipId],
            storeAs: `drivers_${championshipId}`,
        },
        true
    );

    const driversStandings = useMemo(() => {
        return (championship.driversStandings || [])
            .filter(
                (driverStandings) =>
                    !championship.isMultiClass ||
                    driverStandings.className === selectedClass
            )
            .sort((d1, d2) => (d1.position < d2.position ? -1 : 1));
    }, [
        championship.driversStandings,
        championship.isMultiClass,
        selectedClass,
    ]);

    const teamsStandings = useMemo(() => {
        return (championship.teamsStandings || [])
            .filter(
                (teamStandings) =>
                    !championship.isMultiClass ||
                    teamStandings.className === selectedClass
            )
            .sort((d1, d2) => (d1.position < d2.position ? -1 : 1));
    }, [championship.isMultiClass, championship.teamsStandings, selectedClass]);

    const {
        uploading,
        updateChampionshipResults,
    } = useUpdateChampionshipResults();

    const { updating, updateResults } = useUpdateResults();

    return (
        <PageLayout
            showInBoxCard={false}
            boxCardProps={{
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Title
                        text={`${championship.name} - Classificação geral`}
                    />
                </Grid>
                <Grid item xs={12} hidden={!loggedUser.isAdmin}>
                    <FormButton
                        loading={uploading}
                        component="span"
                        color="primary"
                        onClick={() => updateChampionshipResults(championship)}
                    >
                        Atualizar classificação geral
                    </FormButton>
                </Grid>
                <Grid item xs={12} hidden={!championship.isMultiClass}>
                    <Box>
                        <Tabs
                            variant={isDownMd ? "scrollable" : "fullWidth"}
                            centered={!isDownMd}
                            indicatorColor="primary"
                            textColor="primary"
                            scrollButtons="on"
                            value={selectedClass}
                            onChange={selectClassTab(setSelectedClass)}
                        >
                            {classes.map(({ id, name }) => {
                                return (
                                    <Tab
                                        key={`class_${id}`}
                                        label={id || name}
                                        value={id}
                                        style={{ fontSize: "0.85rem" }}
                                    />
                                );
                            })}
                        </Tabs>
                    </Box>
                </Grid>
                <Grid
                    item
                    md={championship.championshipByTeam ? 6 : 12}
                    xs={12}
                >
                    <DriversStandingsTable
                        championship={championship}
                        driversStandings={driversStandings}
                    />
                </Grid>
                <Grid
                    item
                    md={6}
                    xs={12}
                    hidden={!championship.championshipByTeam}
                >
                    <TeamsStandingsTable
                        championship={championship}
                        teamsStandings={teamsStandings}
                    />
                </Grid>
                <Grid item xs={12}>
                    <PenaltyPointsTable
                        championship={championship}
                        drivers={drivers}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SafeDriversTable
                        championship={championship}
                        drivers={drivers}
                    />
                </Grid>
                <Grid item xs={12} style={{ marginTop: 40 }}>
                    <Box display="flex" width="100%" alignContent="center">
                        <Typography
                            component="h2"
                            variant="h4"
                            color="textPrimary"
                        >
                            Resultados por etapas
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} hidden={!loggedUser.isAdmin}>
                    <FormButton
                        color="primary"
                        loading={updating}
                        onClick={() => {
                            updateResults(championship.id);
                        }}
                    >
                        Reprocessar pontuação de todas as etapas
                    </FormButton>
                </Grid>
                <Grid item xs={12}>
                    <RoundStandings
                        championship={championship}
                        drivers={drivers}
                        selectedClass={selectedClass}
                    />
                </Grid>
            </Grid>
        </PageLayout>
    );
};

export { ChampionshipStandings };
